import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { checklist as checklistApi, files as filesApi, templates as templatesApi, api, formatApiError } from "../lib/api";
import { pickField, detectHeaders } from "../lib/excel";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import ExcelUploader from "../components/ExcelUploader";
import ChecklistLogicDialog from "../components/ChecklistLogicDialog";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileType2, Edit3, Languages, Layers, GripVertical, X as XIcon, Workflow } from "lucide-react";
import { useApp } from "../context/AppContext";
import { localizedQuestion, localizedTemplate, templateColor, TEMPLATE_COLOR_SWATCHES } from "../lib/i18nContent";
import * as XLSX from "xlsx";

export default function ChecklistPage() {
  const { user, can } = useApp();
  const { t, i18n } = useTranslation();
  const pdfInputRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [items, setItems] = useState([]);
  const [tplList, setTplList] = useState([]);
  const [filterTpl, setFilterTpl] = useState("all");
  const [dragTplId, setDragTplId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ template: "Tienda", category: "", question: "", weight: 1, criticality: "Media", response_type: "canales" });
  const [editing, setEditing] = useState(null);
  const [tplDialog, setTplDialog] = useState(false);
  const [tplForm, setTplForm] = useState({ id: null, name: "", description: "", color: "" });
  const [translating, setTranslating] = useState(false);
  const [logicItem, setLogicItem] = useState(null);
  const [logicOpen, setLogicOpen] = useState(false);

  const load = () => checklistApi.list().then(setItems).catch(console.error);
  const loadTemplates = () =>
    templatesApi.list().then(setTplList).catch(console.error);
  useEffect(() => {
    load();
    loadTemplates();
  }, []);

  const onParsed = async (rows) => {
    const list = rows
      .map((r) => ({
        template: String(pickField(r, "Plantilla", "Template", "Tipo") || "Tienda").trim() || "Tienda",
        category: String(pickField(r, "Categoria", "Categoría", "Category", "Cat") || "").trim(),
        question: String(pickField(r, "Pregunta", "Question", "Q", "Item", "Reactivo") || "").trim(),
        weight: parseFloat(pickField(r, "Peso", "Weight", "W", "Ponderacion", "Ponderación")) || 1,
        criticality: String(pickField(r, "Criticidad", "Criticality", "Critico", "Crítico", "Severity", "Severidad") || "Media").trim(),
      }))
      .filter((x) => x.category && x.question);
    if (!list.length) {
      const detected = detectHeaders(rows).join(", ") || "—";
      toast.error(`${t("audit.checklist_headers_error")} · Detectado: ${detected}`);
      return;
    }
    // Normalize criticality value to Alta / Media / Baja
    const validCrit = (v) => {
      const n = v.toLowerCase();
      if (["alta", "high", "h", "1"].includes(n)) return "Alta";
      if (["baja", "low", "l", "3"].includes(n)) return "Baja";
      return "Media";
    };
    list.forEach((x) => { x.criticality = validCrit(x.criticality); });
    await checklistApi.bulk(list, true);
    toast.success(t("audit.checklist_updated", { n: list.length }));
    load();
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Plantilla: "Tienda", Categoria: "Sala", Pregunta: "¿Salas exhibidas con orden y limpieza?", Peso: 2, Criticidad: "Alta" },
      { Plantilla: "Imagen", Categoria: "Recamara", Pregunta: "¿Etiquetas de precio visibles?", Peso: 1, Criticidad: "Media" },
      { Plantilla: "Ventas", Categoria: "Financiamiento", Pregunta: "¿Promociones impresas vigentes?", Peso: 1.5, Criticidad: "Alta" },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Checklist");
    XLSX.writeFile(wb, "plantilla_checklist.xlsx");
  };

  const onPdfSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const uploaded = await filesApi.upload(file, "", "checklist_source");
      const r = await api.post("/checklist/extract_from_file", { file_id: uploaded.id });
      const items = r.data?.items || [];
      if (!items.length) {
        toast.error("No se encontraron preguntas en el archivo");
        return;
      }
      await checklistApi.bulk(items, true);
      toast.success(t("audit.checklist_updated", { n: items.length }));
      load();
    } catch (err) {
      console.error(err);
      toast.error(formatApiError(err, t("common.file_read_error", { reason: "" })));
    } finally {
      setExtracting(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const onCreate = async () => {
    if (!form.category || !form.question) return toast.error(t("checklist_page.required"));
    if (editing) {
      await checklistApi.patch(editing.id, { ...form, weight: parseFloat(form.weight) || 1 });
      toast.success(t("checklist_page.updated"));
    } else {
      await checklistApi.create({ ...form, weight: parseFloat(form.weight) || 1 });
      toast.success(t("checklist_page.added"));
    }
    setForm({ template: form.template || "Tienda", category: "", question: "", weight: 1, criticality: "Media", response_type: "canales" });
    setEditing(null);
    setOpen(false);
    load();
  };

  const startEdit = (q) => {
    setEditing(q);
    setForm({
      template: q.template || "Tienda",
      category: q.category || "",
      question: q.question || "",
      weight: q.weight || 1,
      criticality: q.criticality || "Media",
      response_type: q.response_type || "canales",
    });
    setOpen(true);
  };

  const startNewQuestion = () => {
    setEditing(null);
    setForm({ template: filterTpl !== "all" ? filterTpl : "Tienda", category: "", question: "", weight: 1, criticality: "Media", response_type: "canales" });
    setOpen(true);
  };

  const startNewCategory = () => {
    setEditing(null);
    // Same dialog, but visually nudges the user to type the category name (focus is set).
    setForm({ template: filterTpl !== "all" ? filterTpl : "Tienda", category: "", question: "", weight: 1, criticality: "Media", response_type: "canales" });
    setOpen(true);
  };

  // ===== Drag & drop reorder for the template pill bar =====
  const onDragStartTpl = (id) => (e) => {
    setDragTplId(id);
    e.dataTransfer.effectAllowed = "move";
    // Some browsers require a payload to allow the drop event to fire.
    try { e.dataTransfer.setData("text/plain", id); } catch { /* Firefox private mode */ }
  };
  const onDragOverTpl = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDropTpl = (targetId) => async (e) => {
    e.preventDefault();
    const srcId = dragTplId;
    setDragTplId(null);
    if (!srcId || srcId === targetId) return;
    const srcIdx = tplList.findIndex((t) => t.id === srcId);
    const dstIdx = tplList.findIndex((t) => t.id === targetId);
    if (srcIdx < 0 || dstIdx < 0) return;
    const next = [...tplList];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, moved);
    // Optimistic UI update.
    setTplList(next);
    try {
      await templatesApi.reorder(next.map((tpl) => tpl.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
      loadTemplates(); // rollback to server state
    }
  };
  const onDragEndTpl = () => setDragTplId(null);

  const submitTemplate = async () => {
    if (!tplForm.name.trim()) return toast.error(t("checklist_page.required"));
    try {
      const payload = {
        name: tplForm.name.trim(),
        description: tplForm.description,
        color: tplForm.color || null,
      };
      if (tplForm.id) {
        await templatesApi.patch(tplForm.id, payload);
        toast.success(t("cover.template_created"));
      } else {
        await templatesApi.create(payload);
        toast.success(t("cover.template_created"));
      }
      setTplDialog(false);
      setTplForm({ id: null, name: "", description: "", color: "" });
      loadTemplates();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const editTemplate = (tpl) => {
    setTplForm({
      id: tpl.id,
      name: tpl.name,
      description: tpl.description || "",
      color: tpl.color || "",
    });
    setTplDialog(true);
  };

  const deleteTemplate = async (tpl) => {
    // First attempt — backend responds with 409 + counts when template is in use.
    const firstConfirm = window.confirm(
      t("checklist_page.confirm_delete_template", { name: tpl.name })
    );
    if (!firstConfirm) return;
    try {
      await templatesApi.remove(tpl.id);
      toast.success(t("checklist_page.template_deleted"));
      loadTemplates();
      load();
      if (filterTpl === tpl.name) setFilterTpl("all");
      return;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object") {
        const cq = detail.checklist_count || 0;
        const ca = detail.audit_count || 0;
        const force = window.confirm(
          t("checklist_page.confirm_delete_template_force", {
            name: tpl.name,
            checklist: cq,
            audits: ca,
          })
        );
        if (!force) return;
        try {
          await templatesApi.remove(tpl.id, true);
          toast.success(t("checklist_page.template_deleted"));
          loadTemplates();
          load();
          if (filterTpl === tpl.name) setFilterTpl("all");
        } catch (err2) {
          toast.error(err2?.response?.data?.detail || "Error");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Error");
      }
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("checklist_page.confirm_delete"))) return;
    await checklistApi.remove(id);
    load();
  };

  const runTranslate = async () => {
    setTranslating(true);
    try {
      const target = String(i18n.language || "").startsWith("en") ? "en" : "es";
      const r = await checklistApi.translate(target);
      toast.success(t("checklist_page.translated", { n: r.translated, total: r.total }));
      load();
    } catch (e) {
      // Show the real backend message when we have one so future bugs are easy
      // to diagnose. Falls back to a generic message that no longer blames the
      // Universal Key by default (most failures were DB/schema, not auth).
      const detail = e?.response?.data?.detail;
      const rawBody = typeof e?.response?.data === "string" ? e.response.data : "";
      const status = e?.response?.status;
      const msg =
        detail ||
        rawBody ||
        (status ? t("checklist_page.translate_error_http", { code: status }) : t("checklist_page.translate_error"));
      toast.error(msg);
    } finally {
      setTranslating(false);
    }
  };

  const canManage = can("edit_checklist");

  const filteredItems = useMemo(() => {
    if (filterTpl === "all") return items;
    return items.filter((q) => (q.template || "Tienda") === filterTpl);
  }, [items, filterTpl]);

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="checklist-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="canales-overline">{t("nav.checklist")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-1">{t("checklist_page.title")}</h1>
          <p className="text-stone-600 mt-2 text-sm max-w-xl">
            {t("checklist_page.subtitle")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-checklist-template">
            <Download size={16} /> {t("stores.template")}
          </Button>
          {canManage && (
            <>
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={onPdfSelected}
                data-testid="checklist-pdf-input"
              />
              <Button
                variant="outline"
                onClick={() => pdfInputRef.current?.click()}
                disabled={extracting}
                className="gap-2"
                data-testid="checklist-pdf-button"
                title={t("checklist_page.pdf_tooltip")}
              >
                <FileType2 size={16} /> {extracting ? t("checklist_page.extracting") : t("checklist_page.pdf_photo")}
              </Button>
              <ExcelUploader onParsed={onParsed} testid="checklist-excel" label={t("audit.upload_checklist")} />
              <Button
                variant="outline"
                onClick={runTranslate}
                disabled={translating}
                className="gap-2"
                data-testid="translate-checklist-btn"
                title={t("checklist_page.translate_tooltip")}
              >
                <Languages size={16} /> {translating ? t("checklist_page.translating") : t("checklist_page.translate", { lang: i18n.language.startsWith("en") ? "EN" : "ES" })}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setTplForm({ id: null, name: "", description: "", color: "" }); setTplDialog(true); }}
                className="gap-2"
                data-testid="new-template-checklist-btn"
              >
                <Layers size={16} /> {t("cover.new_template")}
              </Button>
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                <DialogTrigger asChild>
                  <Button className="brand-button gap-2" data-testid="add-question-button" onClick={startNewQuestion}>
                    <Plus size={16} /> {t("checklist_page.new_question")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editing ? t("checklist_page.edit_question") : t("checklist_page.new_question")}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="canales-overline">{t("audit.template_label")}</label>
                      <Select
                        value={form.template}
                        onValueChange={(v) => setForm({ ...form, template: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="question-template-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {tplList.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.name}>{tpl.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="canales-overline">{t("common.category")}</label>
                      <Input
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="mt-1"
                        placeholder={t("checklist_page.category_placeholder")}
                        data-testid="question-category-input"
                      />
                    </div>
                    <div>
                      <label className="canales-overline">{t("common.question")}</label>
                      <Input
                        value={form.question}
                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                        className="mt-1"
                        data-testid="question-text-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="canales-overline">{t("common.weight")}</label>
                        <Input
                          type="number"
                          step="0.5"
                          value={form.weight}
                          onChange={(e) => setForm({ ...form, weight: e.target.value })}
                          className="mt-1"
                          data-testid="question-weight-input"
                        />
                      </div>
                      <div>
                        <label className="canales-overline">{t("common.criticality")}</label>
                        <Select
                          value={form.criticality}
                          onValueChange={(v) => setForm({ ...form, criticality: v })}
                        >
                          <SelectTrigger className="mt-1" data-testid="question-criticality-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Alta">{t("severity.Alta")}</SelectItem>
                            <SelectItem value="Media">{t("severity.Media")}</SelectItem>
                            <SelectItem value="Baja">{t("severity.Baja")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="canales-overline">{t("checklist_page.response_type")}</label>
                      <Select
                        value={form.response_type || "canales"}
                        onValueChange={(v) => setForm({ ...form, response_type: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="question-response-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="canales">{t("response_types.canales")}</SelectItem>
                          <SelectItem value="good_fair_poor">{t("response_types.good_fair_poor")}</SelectItem>
                          <SelectItem value="pass_fail">{t("response_types.pass_fail")}</SelectItem>
                          <SelectItem value="compliant">{t("response_types.compliant")}</SelectItem>
                          <SelectItem value="safe_risk">{t("response_types.safe_risk")}</SelectItem>
                          <SelectItem value="text">{t("response_types.text")}</SelectItem>
                          <SelectItem value="number">{t("response_types.number")}</SelectItem>
                          <SelectItem value="media">{t("response_types.media")}</SelectItem>
                          <SelectItem value="signature">{t("response_types.signature")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-stone-400 mt-1">{t("checklist_page.response_type_hint")}</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={onCreate} className="brand-button" data-testid="save-question-button">
                      {t("common.save")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <Card className="canales-card mt-8 overflow-x-auto">
        <div className="px-4 pt-4 flex items-center gap-2 flex-wrap" data-testid="checklist-filters">
          <button
            onClick={() => setFilterTpl("all")}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              filterTpl === "all" ? "bg-[#0B1220] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
            data-testid="filter-tpl-all"
          >
            {t("common.all")} <span className="opacity-60 ml-1">{items.length}</span>
          </button>
          {tplList.map((tpl) => {
            const n = items.filter((q) => (q.template || "Tienda") === tpl.name).length;
            const localizedName = localizedTemplate(tpl.name, i18n.language);
            const isDragging = dragTplId === tpl.id;
            return (
              <div
                key={tpl.id}
                className={`inline-flex items-center gap-1 rounded-full ${isDragging ? "opacity-40" : ""} ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
                draggable={canManage}
                onDragStart={canManage ? onDragStartTpl(tpl.id) : undefined}
                onDragOver={canManage ? onDragOverTpl : undefined}
                onDrop={canManage ? onDropTpl(tpl.id) : undefined}
                onDragEnd={canManage ? onDragEndTpl : undefined}
                data-testid={`tpl-pill-wrap-${tpl.name}`}
                title={canManage ? t("checklist_page.drag_to_reorder", "Arrástrame para reordenar") : undefined}
              >
                {canManage && (
                  <GripVertical
                    size={11}
                    className="text-stone-400 pointer-events-none -mr-0.5"
                    aria-hidden
                  />
                )}
                <button
                  onClick={() => setFilterTpl(tpl.name)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                    filterTpl === tpl.name ? "bg-[#0B1220] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                  }`}
                  data-testid={`filter-tpl-${tpl.name}`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full shrink-0 ring-1 ring-white/60"
                    style={{ backgroundColor: tpl.color || "#94A3B8" }}
                    aria-hidden
                    data-testid={`tpl-dot-${tpl.name}`}
                  />
                  {localizedName} <span className="opacity-60 ml-1">{n}</span>
                </button>
                {canManage && (
                  <button
                    onClick={() => editTemplate(tpl)}
                    className="text-stone-400 hover:text-[#1E3A8A] p-0.5"
                    title={t("checklist_page.edit_template")}
                    data-testid={`edit-tpl-${tpl.name}`}
                  >
                    <Edit3 size={11} />
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={() => deleteTemplate(tpl)}
                    className="text-stone-400 hover:text-red-600 p-0.5"
                    title={t("checklist_page.delete_template", "Eliminar plantilla")}
                    data-testid={`delete-tpl-${tpl.name}`}
                  >
                    <XIcon size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("audit.template_label")}</TableHead>
              <TableHead>{t("common.category")}</TableHead>
              <TableHead>{t("common.question")}</TableHead>
              <TableHead>{t("common.weight")}</TableHead>
              <TableHead>{t("common.criticality")}</TableHead>
              <TableHead className="text-right">{t("findings.col_action")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-stone-500">
                  {t("checklist_page.empty")}
                </TableCell>
              </TableRow>
            )}
            {filteredItems.map((q) => {
              const lq = localizedQuestion(q, i18n.language);
              return (
              <TableRow key={q.id} data-testid={`checklist-row-${q.id}`}>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-[#E5EBFA] text-[#1E3A8A]">
                    <span
                      className="inline-block w-2 h-2 rounded-full ring-1 ring-white/60"
                      style={{ backgroundColor: templateColor(q.template || "Tienda", tplList) }}
                      aria-hidden
                    />
                    {localizedTemplate(q.template || "Tienda", i18n.language)}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{lq.category}</TableCell>
                <TableCell className="text-stone-700 max-w-md">{lq.question}</TableCell>
                <TableCell>{q.weight}</TableCell>
                <TableCell>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                      q.criticality === "Alta"
                        ? "bg-red-50 text-red-700 ring-red-600/20"
                        : q.criticality === "Baja"
                        ? "bg-stone-100 text-stone-700 ring-stone-400/20"
                        : "bg-amber-50 text-amber-700 ring-amber-600/20"
                    }`}
                  >
                    {t(`severity.${q.criticality}`, q.criticality)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {canManage && (
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setLogicItem(q); setLogicOpen(true); }}
                        data-testid={`logic-question-${q.id}`}
                        className={(q.logic || []).length ? "border-[#1E3A8A] text-[#1E3A8A]" : "text-stone-500"}
                        title={t("logic.add_logic")}
                      >
                        <Workflow size={14} />
                        {(q.logic || []).length > 0 && (
                          <span className="ml-1 text-[10px] font-bold">{(q.logic || []).length}</span>
                        )}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(q)} data-testid={`edit-question-${q.id}`}>
                        <Edit3 size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDelete(q.id)} data-testid={`delete-question-${q.id}`} className="text-stone-500 hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Template editor dialog */}
      <Dialog open={tplDialog} onOpenChange={(v) => { setTplDialog(v); if (!v) setTplForm({ id: null, name: "", description: "", color: "" }); }}>
        <DialogContent className="max-w-md" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{tplForm.id ? t("checklist_page.edit_template") : t("cover.new_template_dialog")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="canales-overline">{t("cover.template_name")}</label>
              <Input
                value={tplForm.name}
                onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
                className="mt-1"
                data-testid="template-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="canales-overline">{t("cover.template_description")}</label>
              <Input
                value={tplForm.description}
                onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="canales-overline">{t("checklist_page.template_color", "Color")}</label>
              <div className="mt-1.5 flex items-center flex-wrap gap-2" data-testid="template-color-picker">
                {TEMPLATE_COLOR_SWATCHES.map((c) => {
                  const active = (tplForm.color || "").toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setTplForm({ ...tplForm, color: c })}
                      className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${active ? "ring-2 ring-offset-2 ring-stone-800" : "ring-1 ring-stone-300"}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      data-testid={`template-color-${c.replace("#", "")}`}
                    />
                  );
                })}
                <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-stone-500 cursor-pointer">
                  <input
                    type="color"
                    value={tplForm.color || "#1E3A8A"}
                    onChange={(e) => setTplForm({ ...tplForm, color: e.target.value })}
                    className="w-6 h-6 rounded border border-stone-300 cursor-pointer"
                    data-testid="template-color-custom"
                  />
                  <span className="hidden sm:inline">{t("checklist_page.template_color_custom", "Personalizado")}</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={submitTemplate} className="brand-button" data-testid="template-save-button">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChecklistLogicDialog
        open={logicOpen}
        onOpenChange={setLogicOpen}
        question={logicItem}
        onSaved={load}
      />
    </div>
  );
}
